import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import toast from "react-hot-toast"
import invariant from "tiny-invariant"
import { z } from "zod"
import { gql } from "~/__generated__"
import { AgencyRoleEnum } from "~/__generated__/graphql"
import { useSafeMutation } from "~/common/useSafeMutation"
import {
  displayErrors,
  getGraphqlErrors,
  isValidationError,
} from "~/common/validations"
import { Button } from "~/shadcn/ui/button"
import { FloatingLabelInput } from "~/shadcn/ui/floating-label-input"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/shadcn/ui/form"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/shadcn/ui/select"
import { ModalDialogHeader } from "~/ui/ModalDialog"
import { trackEvent } from "~/util/tracking"

const USER_INVITE_TO_AGENCY_MUTATION = gql(`
  mutation UserInviteToAgency($input: UserInviteToAgencyInput!) {
    userInviteToAgency(input: $input) {
      user {
        id
        email
      }
      currentUser {
        ...CurrentUser
      }
    }
  }
`)

const formSchema = z.object({
  email: z.string().email("Invalid email address"),
  agencyRole: z.nativeEnum(AgencyRoleEnum),
})

const ACCESS_LEVELS: {
  value: AgencyRoleEnum
  label: string
  description: string
}[] = [
  {
    value: AgencyRoleEnum.AgencyAdmin,
    label: "Admin",
    description: "Full access to the account and all available actions.",
  },
  {
    value: AgencyRoleEnum.Agent,
    label: "Agent",
    description:
      "Can use the account but cannot manage the team, subscription or company details.",
  },
  {
    value: AgencyRoleEnum.Intern,
    label: "Intern",
    description:
      "Same restrictions as an Agent, plus cannot add, edit, or delete influencer profiles in the roster.",
  },
]

type FormValues = z.infer<typeof formSchema>

type Props = {
  onClose: () => void
  onSuccess?: () => void
}

export const InviteTeamMemberModal = ({ onClose, onSuccess }: Props) => {
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      agencyRole: AgencyRoleEnum.Agent,
    },
  })

  const [inviteUser, { loading }] = useSafeMutation(
    USER_INVITE_TO_AGENCY_MUTATION
  )

  const onSubmit = async (values: FormValues) => {
    const result = await inviteUser({
      variables: {
        input: {
          email: values.email,
          agencyRole: values.agencyRole,
        },
      },
    })

    if (result.errors) {
      displayErrors(result.errors)

      let errors = getGraphqlErrors(result.errors)
      invariant(errors, "Expected errors")

      for (let error of errors) {
        if (isValidationError(error)) {
          if (
            error.extensions.validationErrors.some((e) => e.type === "taken")
          ) {
            form.setError("email", {
              type: "manual",
              message:
                "Sorry, that user exists in our system already and cannot be invited",
            })
          }
        }
      }
      return
    }

    trackEvent("invite_team_member", { role: values.agencyRole })
    toast.success("Team member invited successfully")
    onClose()
    onSuccess?.()
  }

  return (
    <>
      <ModalDialogHeader
        title="Invite a team member"
        subtitle="Send an email invite to a new user."
      />
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col gap-4"
        >
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FloatingLabelInput
                  {...field}
                  id="email"
                  label="Email Address"
                  type="email"
                  data-1p-ignore
                />
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="agencyRole"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Access level</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select access level" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {ACCESS_LEVELS.map((level) => (
                      <SelectItem
                        key={level.value}
                        value={level.value}
                        description={level.description}
                      >
                        {level.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="flex justify-between">
            <Button
              type="button"
              onClick={onClose}
              variant="linkSecondary"
              size="none"
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Sending..." : "Send Invite"}
            </Button>
          </div>
        </form>
      </Form>
    </>
  )
}
